import { useAuth } from "@/_core/hooks/useAuth";
import DashboardLayout from "@/components/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { canAccessFinanceRoute } from "@/finance/financeNav";
import { FinanceAccessDeniedState, FinancePageHeader } from "@/components/finance/ui";
import {
  AlertTriangle,
  ArrowRightLeft,
  Banknote,
  BookOpen,
  FileCheck2,
  Landmark,
  Lock,
  Receipt,
  RefreshCcw,
} from "lucide-react";

const documentFlows = [
  {
    title: "Facturas emitidas",
    description: "Documento fiscal de ingresos generado desde el flujo canonico de hitos y facturacion.",
    source: "billingMilestones y futura entidad de factura emitida",
  },
  {
    title: "Facturas recibidas",
    description: "Documento fiscal de proveedor ya iniciado en FIN-01, con pagos y evidencias vinculadas.",
    source: "supplierInvoices, supplierPayments y documentos privados",
  },
  {
    title: "Facturas rectificativas",
    description: "Documento nuevo con numeracion propia, vinculado a la factura original y sin reescribir el historico.",
    source: "domain proposal previo obligatorio",
  },
  {
    title: "Abonos",
    description: "Ajustes economicos trazables, separados de la modificacion silenciosa de operaciones historicas.",
    source: "domain proposal previo obligatorio",
  },
];

const movementFlows = [
  "Cobros vinculados a facturas emitidas",
  "Pagos vinculados a facturas recibidas",
  "Cuentas bancarias futuras",
  "Conciliacion bancaria",
  "IVA soportado y repercutido",
  "Periodos contables",
];

const reports = [
  "Libro diario",
  "Libro mayor",
  "Perdidas y ganancias",
  "Balance",
  "Cierre de ejercicio",
];

const rectifyingInvoiceRules = [
  "No equivale a cambiar un hito historico.",
  "Debe tener documento, fecha y numeracion propios.",
  "Debe vincularse a la factura original.",
  "Debe mantener trazabilidad economica y auditoria.",
];

function AccessDenied() {
  return (
    <FinanceAccessDeniedState
      title="Acceso no disponible"
      message="Contabilidad y conciliacion todavia no esta disponible para tu perfil."
    />
  );
}

export default function FinanceLedgerView() {
  const { user } = useAuth();
  const allowed = user ? canAccessFinanceRoute("/accounting/ledger", user.role) : false;

  if (!allowed) return <AccessDenied />;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        <FinancePageHeader
          eyebrow="Finanzas y Contabilidad"
          eyebrowIcon={Landmark}
          title="Contabilidad y conciliacion"
          description="Mapa funcional futuro para facturas, abonos, cobros, pagos, IVA, bancos y conciliacion. Esta pantalla no crea asientos, no inventa saldos y no escribe datos contables."
          badgeText="En preparacion"
        />

        <Card className="border-amber-200 bg-amber-50/60">
          <CardContent className="flex flex-col gap-3 px-4 py-4 text-sm text-amber-950 md:flex-row md:items-start">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" />
            <div className="space-y-1">
              <p className="font-medium">Domain proposal obligatorio antes de tablas contables</p>
              <p>
                Cualquier migracion futura para libro diario, conciliacion, periodos contables o rectificativas requiere una propuesta de dominio separada y aprobacion explicita.
              </p>
            </div>
          </CardContent>
        </Card>

        <div className="grid gap-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(320px,0.8fr)]">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Receipt className="h-4 w-4" />
                Documentos fiscales
              </CardTitle>
            </CardHeader>
            <CardContent className="grid gap-3 md:grid-cols-2">
              {documentFlows.map((flow) => (
                <div key={flow.title} className="rounded-md border p-3">
                  <div className="flex items-start justify-between gap-3">
                    <h2 className="text-sm font-semibold">{flow.title}</h2>
                    <Badge variant="secondary">Diseño</Badge>
                  </div>
                  <p className="mt-2 text-sm text-muted-foreground">{flow.description}</p>
                  <p className="mt-3 text-xs text-muted-foreground">Fuente: {flow.source}</p>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <RefreshCcw className="h-4 w-4" />
                Rectificativas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {rectifyingInvoiceRules.map((rule) => (
                  <li key={rule} className="flex gap-2">
                    <FileCheck2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                    <span>{rule}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <ArrowRightLeft className="h-4 w-4" />
                Cobros, pagos e IVA
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {movementFlows.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <BookOpen className="h-4 w-4" />
                Libros e informes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2 text-sm text-muted-foreground">
                {reports.map((item) => (
                  <li key={item} className="flex gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-muted-foreground" />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Banknote className="h-4 w-4" />
                Estado de implementacion
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3 text-sm text-muted-foreground">
              <p>Sin asientos contables automaticos.</p>
              <Separator />
              <p>Sin conciliacion bancaria real.</p>
              <Separator />
              <p>Sin saldos bancarios inventados.</p>
              <Separator />
              <p>Sin migraciones ni tablas nuevas en esta fase.</p>
            </CardContent>
          </Card>
        </div>
      </div>
    </DashboardLayout>
  );
}
